import EventLayout from '@/components/layouts/EventLayout'
import { PageHeader, ZeroState } from '@/components/patterns'
import ContentSection from '@/components/shared/ContentSection'
import { Button, Card, ConfirmModal, Input, message, Spin } from '@/components/ui'
import {
  PlusCircleFilled,
  CopyOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { useState, useRef, useCallback, useEffect } from 'react'
import axios from '@/lib/axios'
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor'
import { buildMergeTags, type MergeTagOptions } from '@/lib/mergeTags'
import AddTabFromEventModal, { type ImportedSourceTab } from './components/AddTabFromEventModal'

interface Tab {
  id: string
  name: string
  content: {
    html: string
    design: Record<string, unknown>
  }
  position: number
}

interface IndexProps {
  eventId: number
  eventName: string
  eventSlug: string
  tabs: Tab[]
  mergeTagOptions?: MergeTagOptions
}

export default function Index({ eventSlug, tabs: initialTabs, mergeTagOptions }: IndexProps) {
  const [tabs, setTabs] = useState<Tab[]>(initialTabs)
  const [activeTabId, setActiveTabId] = useState<string>(tabs[0]?.id || '')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editorReady, setEditorReady] = useState(false)
  const [editingTabId, setEditingTabId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; tabId: string | null; tabName: string }>({
    visible: false,
    tabId: null,
    tabName: '',
  })
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [addFromEventModalOpen, setAddFromEventModalOpen] = useState(false)

  const emailEditorRef = useRef<EditorRef | null>(null)
  const suppressUnsavedFlagRef = useRef(false)
  const lastLoadedTabIdRef = useRef<string | null>(null)

  const activeTab = tabs.find((t) => t.id === activeTabId)

  // Load design into editor when tab changes or editor becomes ready
  useEffect(() => {
    if (editorReady && activeTab && emailEditorRef.current?.editor) {
      const isSameTab = lastLoadedTabIdRef.current === activeTab.id
      if (isSameTab && hasUnsavedChanges) {
        // Avoid overwriting in-progress edits if state changes elsewhere
        return
      }

      // Avoid marking the load as a user change
      suppressUnsavedFlagRef.current = true
      const design = activeTab.content.design
      if (design && Object.keys(design).length > 0) {
        emailEditorRef.current.editor.loadDesign(design as unknown as Parameters<typeof emailEditorRef.current.editor.loadDesign>[0])
      } else {
        // Load empty design for new tabs
        emailEditorRef.current.editor.loadDesign({
          body: {
            rows: [],
          },
        } as unknown as Parameters<typeof emailEditorRef.current.editor.loadDesign>[0])
      }

      setHasUnsavedChanges(false)
      lastLoadedTabIdRef.current = activeTab.id
    }
  }, [activeTabId, editorReady, activeTab, hasUnsavedChanges])

  // Track design updates to flag unsaved changes
  useEffect(() => {
    if (!editorReady || !emailEditorRef.current?.editor) return

    const editor = emailEditorRef.current.editor
    const handleDesignUpdated = () => {
      if (suppressUnsavedFlagRef.current) {
        suppressUnsavedFlagRef.current = false
        return
      }
      setHasUnsavedChanges(true)
    }

    editor.addEventListener('design:updated', handleDesignUpdated)

    return () => {
      editor.removeEventListener?.('design:updated', handleDesignUpdated)
    }
  }, [editorReady])

  const handleEditorReady: EmailEditorProps['onReady'] = () => {
    setEditorReady(true)
  }

  const handleAddTab = async () => {
    if (hasUnsavedChanges) {
      message.warning('Please save your changes before adding a new tab')
      return
    }

    setLoading(true)
    try {
      const newTabName = `Tab ${tabs.length + 1}`
      const response = await axios.post(`/events/${eventSlug}/informational-tabs/tabs`, {
        tab: {
          name: newTabName,
          content: {
            html: '',
            design: {},
          },
        },
      })

      if (response.data.success) {
        message.success(response.data.message)
        const newTab: Tab = response.data.tab
        setTabs((prev) => [...prev, newTab].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id)))
        setActiveTabId(newTab.id)
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { errors?: string[] } } }
      message.error(axiosError.response?.data?.errors?.join(', ') || 'Failed to add tab')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenAddFromEventModal = () => {
    if (hasUnsavedChanges) {
      message.warning('Please save your changes before adding a tab from another event')
      return
    }

    setAddFromEventModalOpen(true)
  }

  const handleImportTabFromEvent = async ({ tab, sourceEventName }: ImportedSourceTab) => {
    setLoading(true)
    try {
      const response = await axios.post(`/events/${eventSlug}/informational-tabs/tabs`, {
        tab: {
          name: tab.name,
          content: {
            html: tab.content.html,
            design: tab.content.design,
          },
        },
      })

      if (response.data.success) {
        const newTab: Tab = response.data.tab
        setTabs((prev) =>
          [...prev, newTab].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))
        )
        setActiveTabId(newTab.id)
        message.success(`Tab "${newTab.name}" added from "${sourceEventName}"`)
        return true
      }
      return false
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { errors?: string[] } } }
      message.error(axiosError.response?.data?.errors?.join(', ') || 'Failed to import tab')
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleTabClick = (tabId: string) => {
    if (tabId === activeTabId) return

    if (hasUnsavedChanges) {
      message.warning('Please save your changes before switching tabs')
      return
    }

    setActiveTabId(tabId)
  }

  const handleMoveTab = async (tabId: string, direction: 'left' | 'right') => {
    const tabIndex = tabs.findIndex((t) => t.id === tabId)
    if (tabIndex === -1) return

    const targetIndex = direction === 'left' ? tabIndex - 1 : tabIndex + 1
    if (targetIndex < 0 || targetIndex >= tabs.length) return

    const newPosition = tabs[targetIndex].position

    setLoading(true)
    try {
      const response = await axios.patch(`/events/${eventSlug}/informational-tabs/tabs/${tabId}/reorder`, {
        position: newPosition,
      })

      if (response.data.success) {
        message.success(response.data.message)
        const reorderedTabs: Tab[] = response.data.tabs
        setTabs(reorderedTabs)
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { errors?: string[] } } }
      message.error(axiosError.response?.data?.errors?.join(', ') || 'Failed to reorder tab')
    } finally {
      setLoading(false)
    }
  }

  const handleStartEditName = (tab: Tab) => {
    setEditingTabId(tab.id)
    setEditingName(tab.name)
  }

  const handleSaveTabName = async () => {
    if (!editingTabId || !editingName.trim()) {
      message.error('Tab name cannot be empty')
      setEditingTabId(null)
      return
    }

    setLoading(true)
    try {
      const response = await axios.patch(`/events/${eventSlug}/informational-tabs/tabs/${editingTabId}`, {
        tab: {
          name: editingName,
        },
      })

      if (response.data.success) {
        message.success(response.data.message)
        setTabs((prev) => prev.map((t) => (t.id === editingTabId ? { ...t, name: editingName } : t)))
        setEditingTabId(null)
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { errors?: string[] } } }
      message.error(axiosError.response?.data?.errors?.join(', ') || 'Failed to update tab name')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelEditName = () => {
    setEditingTabId(null)
    setEditingName('')
  }

  const handleDeleteTabClick = (tab: Tab) => {
    setDeleteModal({
      visible: true,
      tabId: tab.id,
      tabName: tab.name,
    })
  }

  const handleDeleteTab = async () => {
    if (!deleteModal.tabId) return

    setLoading(true)
    try {
      const response = await axios.delete(`/events/${eventSlug}/informational-tabs/tabs/${deleteModal.tabId}`)

      if (response.data.success) {
        message.success(response.data.message)
        setTabs((prevTabs) => {
          const nextTabs = prevTabs.filter((t) => t.id !== deleteModal.tabId)

          if (deleteModal.tabId === activeTabId) {
            const deletedIndex = prevTabs.findIndex((t) => t.id === deleteModal.tabId)
            const fallbackTab = nextTabs[deletedIndex] || nextTabs[deletedIndex - 1] || nextTabs[0]
            setActiveTabId(fallbackTab?.id || '')
          }

          return nextTabs
        })
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { errors?: string[] } } }
      message.error(axiosError.response?.data?.errors?.join(', ') || 'Failed to delete tab')
    } finally {
      setLoading(false)
      setDeleteModal({ visible: false, tabId: null, tabName: '' })
    }
  }

  const handleSaveChanges = useCallback(() => {
    if (!emailEditorRef.current?.editor || !activeTabId) return

    setSaving(true)
    emailEditorRef.current.editor.exportHtml(async (data) => {
      const { html, design } = data

      try {
        const response = await axios.patch(`/events/${eventSlug}/informational-tabs/tabs/${activeTabId}`, {
          tab: {
            content: {
              html,
              design,
            },
          },
        })

        if (response.data.success) {
          message.success('Changes have been saved.')
          setHasUnsavedChanges(false)
          // Update local state
          setTabs((prev) =>
            prev.map((t) =>
              t.id === activeTabId
                ? {
                    ...t,
                    content: { html, design: design as Record<string, unknown> },
                  }
                : t
            )
          )
        }
      } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { errors?: string[] } } }
        message.error(axiosError.response?.data?.errors?.join(', ') || 'Failed to save changes')
      } finally {
        setSaving(false)
      }
    })
  }, [activeTabId, eventSlug])

  // Zero state when no tabs exist
  if (tabs.length === 0) {
    return (
      <EventLayout>
        <PageHeader
          title="Informational Tabs"
          subtitle="Create multiple informational tabs for the Attendee App homepage."
        />
        <ContentSection padding="lg">
          <Card className="min-h-[400px] flex items-center justify-center">
            <ZeroState
              icon={<FileTextOutlined className="text-5xl text-blue-500" />}
              title="No Tabs Yet"
              description="Create your first informational tab to get started."
              action={{
                label: 'Add Tab',
                icon: <PlusCircleFilled />,
                onClick: handleAddTab,
              }}
            />
            <div className="-mt-12 text-center">
              <Button
                variant="secondary"
                icon={<CopyOutlined />}
                onClick={handleOpenAddFromEventModal}
                loading={loading}
              >
                Add Tab From Event
              </Button>
            </div>
          </Card>
        </ContentSection>

        <AddTabFromEventModal
          open={addFromEventModalOpen}
          eventSlug={eventSlug}
          onClose={() => setAddFromEventModalOpen(false)}
          onImport={handleImportTabFromEvent}
        />
      </EventLayout>
    )
  }

  return (
    <EventLayout>
      <PageHeader
        title="Informational Tabs"
        subtitle="Create multiple informational tabs for the Attendee App homepage."
      />

      <ContentSection padding="lg">
        {/* Tab Bar */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {tabs.map((tab, index) => {
            const isActive = tab.id === activeTabId
            const isEditing = editingTabId === tab.id
            const isFirst = index === 0
            const isLast = index === tabs.length - 1

            return (
              <div
                key={tab.id}
                className={`
                  flex items-center gap-0.5 px-2 py-0.5 border transition-colors
                  ${isActive ? 'border-ava-blue-02 shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}
                `}
              >
                {/* Move Left Button */}
                <button
                  onClick={() => handleMoveTab(tab.id, 'left')}
                  disabled={isFirst || loading}
                  className={`flex h-7 items-center justify-center rounded ${isFirst ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  title="Move left"
                >
                  <LeftOutlined className="text-xs align-middle" />
                </button>

                {/* Tab Name */}
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <Input
                      size="small"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTabName()
                        if (e.key === 'Escape') handleCancelEditName()
                      }}
                      autoFocus
                      className="w-24"
                    />
                    <button
                      onClick={handleSaveTabName}
                      className="p-1 rounded hover:bg-gray-600/20 text-green-500"
                      title="Save"
                    >
                      <CheckOutlined className="text-xs" />
                    </button>
                    <button
                      onClick={handleCancelEditName}
                      className="p-1 rounded hover:bg-gray-600/20 text-red-500"
                      title="Cancel"
                    >
                      <CloseOutlined className="text-xs" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleTabClick(tab.id)}
                    onDoubleClick={() => handleStartEditName(tab)}
                    className={`text-sm px-2 h-7 inline-flex items-center font-medium min-w-[60px] text-center rounded ${isActive ? 'text-ava-blue-02' : 'text-gray-700 hover:text-gray-900'}`}
                    title="Double-click to rename"
                  >
                    {tab.name}
                  </button>
                )}

                {/* Move Right Button */}
                <button
                  onClick={() => handleMoveTab(tab.id, 'right')}
                  disabled={isLast || loading}
                  className={`flex h-7 items-center justify-center rounded ${isLast ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  title="Move right"
                >
                  <RightOutlined className="text-xs align-middle" />
                </button>

                {/* Edit & Delete Buttons (only for active tab) */}
                {isActive && !isEditing && (
                  <>
                    <button
                      onClick={() => handleStartEditName(tab)}
                      className="flex h-7 w-7 items-center justify-center rounded hover:bg-gray-100 ml-0.5"
                      title="Rename tab"
                    >
                      <EditOutlined className="text-xs align-middle" />
                    </button>
                    <button
                      onClick={() => handleDeleteTabClick(tab)}
                      className="flex h-7 w-7 items-center justify-center rounded hover:bg-gray-100 text-red-500 ml-0.5"
                      title="Delete tab"
                    >
                      <DeleteOutlined className="text-xs align-middle" />
                    </button>
                  </>
                )}
              </div>
            )
          })}

          {/* Add Tab Button */}
          <Button variant="secondary" icon={<PlusCircleFilled />} onClick={handleAddTab} loading={loading}>
            Add Tab
          </Button>
          <Button
            variant="secondary"
            icon={<CopyOutlined />}
            onClick={handleOpenAddFromEventModal}
            loading={loading}
          >
            Add Tab From Event
          </Button>
        </div>

        {/* Editor Area */}
        <Card className="min-h-[600px] relative">
          {!editorReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <Spin size="large" tip="Loading editor..." />
            </div>
          )}
          <div style={{ height: '600px' }}>
            <EmailEditor
              ref={emailEditorRef}
              onReady={handleEditorReady}
              minHeight="600px"
              options={{
                displayMode: 'web',
                features: {
                  textEditor: {
                    spellChecker: true,
                  },
                },
                appearance: {
                  theme: 'light',
                },
                mergeTags: buildMergeTags(mergeTagOptions),
              }}
            />
          </div>
        </Card>

        {/* Save Button */}
        <div className="mt-4">
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            loading={saving}
            disabled={!editorReady}
          >
            Save Changes
          </Button>
          {hasUnsavedChanges && (
            <span className="ml-3 text-sm text-orange-500">You have unsaved changes</span>
          )}
        </div>
      </ContentSection>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModal.visible}
        title="Delete Tab"
        onConfirm={handleDeleteTab}
        onClose={() => setDeleteModal({ visible: false, tabId: null, tabName: '' })}
        confirmText="Delete"
        danger
      >
        Are you sure you want to delete the tab "{deleteModal.tabName}"? This action cannot be undone.
      </ConfirmModal>

      <AddTabFromEventModal
        open={addFromEventModalOpen}
        eventSlug={eventSlug}
        onClose={() => setAddFromEventModalOpen(false)}
        onImport={handleImportTabFromEvent}
      />
    </EventLayout>
  )
}
